import React, { useState } from 'react';
import { login02 } from '../../imagepath';
import { Link, useNavigate } from 'react-router-dom';
import { registerPatient } from '../../../api/auth.api';
import FeatherIcon from 'feather-icons-react';
import { translateError } from '../../../utils/errorTranslations';

const RegisterPatient = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    yearOfBirth: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '+359',
    gender: 'MALE',
    password: '',
    confirmPassword: '',
    agreeToTerms: false
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);

  // Live password requirement checks (mirror the Cognito password policy)
  const passwordRules = [
    { key: 'length', label: 'Поне 8 символа', ok: formData.password.length >= 8 },
    { key: 'upper', label: 'Главна буква (A-Z)', ok: /[A-Z]/.test(formData.password) },
    { key: 'lower', label: 'Малка буква (a-z)', ok: /[a-z]/.test(formData.password) },
    { key: 'digit', label: 'Цифра (0-9)', ok: /\d/.test(formData.password) },
    { key: 'special', label: 'Специален символ (@$!%*?&^#)', ok: /[@$!%*?&^#]/.test(formData.password) },
  ];
  const showPasswordRules = passwordFocused || formData.password.length > 0;

  // Live validation checks
  const isPhoneValid = formData.phone.length === 13 && /^\+359\d{9}$/.test(formData.phone);
  const isPhoneStarted = formData.phone.length > 4;
  const passwordsMatch = formData.confirmPassword ? formData.password === formData.confirmPassword : true;
  const currentYear = new Date().getFullYear();
  const yearValue = formData.yearOfBirth ? parseInt(formData.yearOfBirth) : null;
  const isYearValid = yearValue && String(formData.yearOfBirth).length === 4 && yearValue >= 1900 && yearValue <= currentYear;
  const isYearStarted = formData.yearOfBirth.length > 0;

  const validatePhone = (phone) => {
    if (!phone.startsWith('+359')) {
      return 'Телефонът трябва да започва с +359';
    }
    if (!/^\+359\d{9}$/.test(phone)) {
      return 'Невалиден телефонен номер (формат: +359XXXXXXXXX)';
    }
    return null;
  };

  const validateForm = () => {
    const newErrors = {};

    // Year of birth is required
    if (!formData.yearOfBirth) {
      newErrors.yearOfBirth = 'Моля, въведете година на раждане';
    } else {
      const year = parseInt(formData.yearOfBirth);
      const currentYear = new Date().getFullYear();
      if (year < 1900 || year > currentYear) {
        newErrors.yearOfBirth = `Годината трябва да е между 1900 и ${currentYear}`;
      }
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Моля, въведете собствено име';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Моля, въведете фамилия';
    }

    if (!formData.email) {
      newErrors.email = 'Моля, въведете имейл';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Невалиден имейл адрес';
    }

    const phoneError = validatePhone(formData.phone);
    if (phoneError) newErrors.phone = phoneError;

    if (!formData.password) {
      newErrors.password = 'Моля, въведете парола';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Паролата трябва да съдържа поне 8 символа';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&^#])/.test(formData.password)) {
      newErrors.password = 'Паролата трябва да съдържа главни и малки букви, цифри и специален символ (@$!%*?&^#)';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Паролите не съвпадат';
    }

    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'Трябва да приемете условията за ползване';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value;

    if (!value.startsWith('+359')) {
      value = '+359';
    }

    const digitsOnly = value.slice(4).replace(/\D/g, '');
    value = '+359' + digitsOnly.slice(0, 9);

    setFormData(prev => ({ ...prev, phone: value }));
    if (errors.phone) {
      setErrors(prev => ({ ...prev, phone: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      
      await registerPatient({
        yearOfBirth: parseInt(formData.yearOfBirth),
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        gender: formData.gender,
        password: formData.password
      });

      
      // Redirect to confirmation page with email
      navigate('/confirm-email', { state: { email: formData.email } });
    } catch (error) {
      console.error('Registration error:', error);

      let errorMessage = 'Възникна грешка при регистрацията';

      if (error.data?.message) {
        errorMessage = translateError(error.data.message);
      } else if (error.message) {
        errorMessage = translateError(error.message);
      }

      setErrors({ submit: errorMessage });
      setLoading(false);
      return;
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="main-wrapper login-body">
      <div className="container-fluid px-0">
        <div className="row">
          <div className="col-lg-6 login-wrap">
            <div className="login-sec">
              <div className="log-img">
                <img className="img-fluid" src={login02} alt="Logo" />
              </div>
            </div>
          </div>

          <div className="col-lg-6 login-wrap-bg">
            <div className="login-wrapper">
              <div className="loginbox">
                <div className="login-right">
                  <div className="login-right-wrap">
                    <h2>Регистрация на пациент</h2>
                    <p className="text-muted mb-4">
                      Създайте акаунт за консултации
                    </p>

                    <form onSubmit={handleSubmit}>
                      {errors.submit && (
                        <div className="alert alert-danger" role="alert">
                          {errors.submit}
                        </div>
                      )}

                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label>
                              Име <span className="login-danger">*</span>
                            </label>
                            <input
                              className={'form-control ' + (errors.firstName ? 'is-invalid' : '')}
                              type="text"
                              name="firstName"
                              value={formData.firstName}
                              onChange={handleChange}
                              placeholder="Име"
                            />
                            {errors.firstName && <div className="invalid-feedback d-block">{errors.firstName}</div>}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group">
                            <label>
                              Фамилия <span className="login-danger">*</span>
                            </label>
                            <input
                              className={'form-control ' + (errors.lastName ? 'is-invalid' : '')}
                              type="text"
                              name="lastName"
                              value={formData.lastName}
                              onChange={handleChange}
                              placeholder="Фамилия"
                            />
                            {errors.lastName && <div className="invalid-feedback d-block">{errors.lastName}</div>}
                          </div>
                        </div>
                      </div>

                      <div className="form-group">
                        <label>
                          Година на раждане <span className="login-danger">*</span>
                        </label>
                        <input
                          className={'form-control ' + (errors.yearOfBirth ? 'is-invalid' : '')}
                          type="number"
                          name="yearOfBirth"
                          value={formData.yearOfBirth}
                          onChange={handleChange}
                          placeholder="1990"
                          min="1900"
                          max={currentYear}
                        />
                        {errors.yearOfBirth && <div className="invalid-feedback d-block">{errors.yearOfBirth}</div>}
                        {isYearStarted && !isYearValid && !errors.yearOfBirth && (
                          <small className="text-danger">
                            Въведете валидна година (1900-{currentYear})
                          </small>
                        )}
                      </div>

                      <div className="form-group">
                        <label>
                          Имейл <span className="login-danger">*</span>
                        </label>
                        <input
                          className={'form-control ' + (errors.email ? 'is-invalid' : '')}
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="mail@example.com"
                        />
                        {errors.email && <div className="invalid-feedback d-block">{errors.email}</div>}
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label>
                              Телефон <span className="login-danger">*</span>
                            </label>
                            <input
                              className={'form-control ' + (errors.phone ? 'is-invalid' : '')}
                              type="tel"
                              name="phone"
                              value={formData.phone}
                              onChange={handlePhoneChange}
                              placeholder="+359888123456"
                            />
                            {errors.phone && <div className="invalid-feedback d-block">{errors.phone}</div>}
                            {isPhoneStarted && !isPhoneValid && !errors.phone && (
                              <small className="text-danger">
                                Въведете 9 цифри след +359
                              </small>
                            )}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group">
                            <label>
                              Пол <span className="login-danger">*</span>
                            </label>
                            <select
                              className="form-control"
                              name="gender"
                              value={formData.gender}
                              onChange={handleChange}
                            >
                              <option value="MALE">Мъж</option>
                              <option value="FEMALE">Жена</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <div className="form-group">
                        <label>
                          Парола <span className="login-danger">*</span>
                        </label>
                        <input
                          className={'form-control pass-input ' + (errors.password ? 'is-invalid' : '')}
                          type={showPassword ? 'text' : 'password'}
                          name="password"
                          value={formData.password}
                          onChange={handleChange}
                          onFocus={() => setPasswordFocused(true)}
                          onBlur={() => setPasswordFocused(false)}
                          placeholder="Минимум 8 символа"
                        />
                        <span
                          className="toggle-password"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          <FeatherIcon icon={showPassword ? 'eye' : 'eye-off'} size={16} />
                        </span>
                        {errors.password && <div className="invalid-feedback d-block">{errors.password}</div>}
                        {showPasswordRules ? (
                          <ul
                            className="password-rules"
                            style={{ listStyle: 'none', padding: 0, margin: '8px 0 0' }}
                          >
                            {passwordRules.map((rule) => (
                              <li
                                key={rule.key}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  fontSize: '12px',
                                  lineHeight: '1.7',
                                  color: rule.ok ? '#52c41a' : '#8c8c8c',
                                  transition: 'color .15s ease',
                                }}
                              >
                                <FeatherIcon icon={rule.ok ? 'check-circle' : 'circle'} size={14} />
                                {rule.label}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <small className="text-muted d-block" style={{ marginTop: '4px' }}>
                            Мин. 8 символа: главни и малки букви, цифри и специален символ (@$!%*?&^#)
                          </small>
                        )}
                      </div>

                      <div className="form-group">
                        <label>
                          Потвърдете парола <span className="login-danger">*</span>
                        </label>
                        <input
                          className={'form-control pass-input ' + (errors.confirmPassword ? 'is-invalid' : '')}
                          type={showConfirmPassword ? 'text' : 'password'}
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          placeholder="Повторете паролата"
                        />
                        <span
                          className="toggle-password"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        >
                          <FeatherIcon icon={showConfirmPassword ? 'eye' : 'eye-off'} size={16} />
                        </span>
                        {errors.confirmPassword && <div className="invalid-feedback d-block">{errors.confirmPassword}</div>}
                        {formData.confirmPassword && !passwordsMatch && !errors.confirmPassword && (
                          <small className="text-danger">
                            Паролите не съвпадат
                          </small>
                        )}
                      </div>

                      <div className="forgotpass">
                        <div className="remember-me">
                          <label className="d-flex align-items-start" style={{ cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              name="agreeToTerms"
                              checked={formData.agreeToTerms}
                              onChange={handleChange}
                              style={{ marginTop: '3px', marginRight: '8px', cursor: 'pointer' }}
                            />
                            <span>
                              Съгласен съм с <Link to="/terms">условията за ползване</Link> и <Link to="/privacy">политиката за поверителност</Link>
                            </span>
                          </label>
                          {errors.agreeToTerms && <div className="text-danger small mt-1">{errors.agreeToTerms}</div>}
                        </div>
                      </div>

                      {errors.submit && (
                        <div className="alert alert-danger" role="alert">
                          {errors.submit}
                        </div>
                      )}

                      <div className="form-group login-btn">
                        <button
                          className="btn btn-primary btn-block"
                          type="submit"
                          disabled={loading}
                        >
                          {loading ? 'Регистрация...' : 'Регистрация'}
                        </button>
                      </div>
                    </form>

                    <div className="next-sign">
                      <p className="account-subtitle">
                        Имате акаунт? <Link to="/login">Вход</Link>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPatient;
